import { getColumnsForCsvUpload } from '@doss/common-all'
import {
  DataType,
  PermissionObjectType,
  PermissionType,
  RoleType,
} from '../generated/apollo-operations'
import { SimpleUserRole } from '../hooks/types'

/** The parts of a chart that decide whether the user can configure it */
type ConfigurableChart = {
  table: { id: string }
  views: { parentWorkspace: { id: string } }[]
}

/**
 * Table shape needed to align upload permission with {@link getColumnsForCsvUpload} (Create).
 * CSV does not include formula columns or index-autogenerated columns, so we must not require
 * EDIT_CELL on those columns — they are often not grantable and would hide the upload button.
 */
type TableForCsvUploadPermission = {
  id: string
  columns: Array<{
    id: string
    order: number
    indexAutogenerate: boolean
    type: DataType
  }>
}

interface TablePermissions {
  canAddRow: boolean
  canDeleteRow: boolean
  hasEditAllCells: boolean
  hasViewAllCells: boolean
  canConfigureTableTabs: boolean
  canConfigurePivotTables: boolean
  viewableColumnIds: Set<string>
  viewableTabIds: Set<string>
}

/**
 * Manages user permissions for tables, columns, and workspaces. It checks whether a user is an admin
 * or has specific permissions like adding or deleting rows, editing cells (all or column-specific),
 * configuring table tabs, and editing workspaces.
 *
 * Key methods include:
 * - `canAddRowToTable()`
 * - `canDeleteRowFromTable()`
 * - `canEditCell()`
 * - `canEditWorkspace()`
 * - `canUploadDataToTable()`
 * - `canConfigureTableTabs()`
 * - `canConfigurePivotTables()`
 */
export class UserPermissionsManager {
  private tablePermissions: Map<string, TablePermissions> = new Map()
  private editableColumnIds: Set<string> = new Set()
  private editableWorkspaceIds: Set<string> = new Set()
  private isAdmin = false

  constructor(userRoles: SimpleUserRole[]) {
    // Check if user has admin privileges first
    this.isAdmin = userRoles.some(
      (role) => role.roleType === RoleType.ADMIN || role.roleType === RoleType.INTERNAL_DOSS,
    )

    if (!this.isAdmin) {
      // Only process detailed permissions if not an admin
      this.processPermissions(userRoles)
    }
  }

  // Helper method to ensure table permissions exist
  private ensureTablePermissions(tableId: string): TablePermissions {
    let tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      tablePerms = {
        canAddRow: false,
        canDeleteRow: false,
        hasEditAllCells: false,
        hasViewAllCells: false,
        canConfigureTableTabs: false,
        canConfigurePivotTables: false,
        viewableColumnIds: new Set<string>(),
        viewableTabIds: new Set<string>(),
      }
      this.tablePermissions.set(tableId, tablePerms)
    }
    return tablePerms
  }

  private processPermissions(roles: SimpleUserRole[]) {
    // Process all permissions in a single pass
    for (const role of roles) {
      for (const permission of role.permissions) {
        if (permission.objectType === PermissionObjectType.TABLE && permission.table) {
          const tableId = permission.table.id

          // Fetch existing tablePermissions or initialize
          const tablePerms = this.ensureTablePermissions(tableId)

          if (permission.permissionType === PermissionType.ADD_ROW) {
            tablePerms.canAddRow = true
          }
          if (permission.permissionType === PermissionType.EDIT_ALL_TABLE_CELLS) {
            tablePerms.hasEditAllCells = true
          }
          if (permission.permissionType === PermissionType.VIEW_ALL_TABLE_CELLS) {
            tablePerms.hasViewAllCells = true
          }
          if (permission.permissionType === PermissionType.DELETE_ROW) {
            tablePerms.canDeleteRow = true
          }
          if (permission.permissionType === PermissionType.CONFIGURE_TABLE_TABS) {
            tablePerms.canConfigureTableTabs = true
          }
          if (permission.permissionType === PermissionType.CONFIGURE_PIVOT_TABLES) {
            tablePerms.canConfigurePivotTables = true
          }
        } else if (
          permission.objectType === PermissionObjectType.TABLE_COLUMN &&
          permission.tableColumn
        ) {
          if (permission.permissionType === PermissionType.EDIT_CELL) {
            this.editableColumnIds.add(permission.tableColumn.id)
            // When a column is editable, it's also viewable
            const tableId = permission.tableColumn.table.id
            const tablePerms = this.ensureTablePermissions(tableId)
            tablePerms.viewableColumnIds.add(permission.tableColumn.id)
          } else if (permission.permissionType === PermissionType.VIEW_COLUMN) {
            const tableId = permission.tableColumn.table.id
            const tablePerms = this.ensureTablePermissions(tableId)
            tablePerms.viewableColumnIds.add(permission.tableColumn.id)
          }
        } else if (
          permission.objectType === PermissionObjectType.WORKSPACE &&
          permission.workspace
        ) {
          if (permission.permissionType === PermissionType.EDIT_WORKSAPCE) {
            this.editableWorkspaceIds.add(permission.workspace.id)
          }
        } else if (
          permission.objectType === PermissionObjectType.TABLE_TAB &&
          permission.tableTab
        ) {
          if (permission.permissionType === PermissionType.VIEW_TABLE_TAB_ROWS) {
            const tableId = permission.tableTab.table.id
            const tablePerms = this.ensureTablePermissions(tableId)
            tablePerms.viewableTabIds.add(permission.tableTab.id)
          }
        }
      }
    }
  }

  canAddRowToTable(tableId: string, requiredColumnIds: string[]): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    // Must have ADD_ROW permission
    if (!tablePerms.canAddRow) {
      return false
    }

    // Check if user has EDIT_ALL_TABLE_CELLS
    if (tablePerms.hasEditAllCells) {
      return true
    }

    // Check if user has EDIT_CELL permission for all required columns
    return requiredColumnIds.every((columnId) => this.editableColumnIds.has(columnId))
  }

  canDeleteRowFromTable(tableId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    // Must have DELETE_ROW permission
    return tablePerms.canDeleteRow
  }

  canEditCell(tableId: string, columnId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    if (tablePerms.hasEditAllCells) {
      return true
    }

    // If user does not have permission to edit all cells, have to check if they have
    // edit cells permissions on the column
    return this.editableColumnIds.has(columnId)
  }

  canEditWorkspace(workspaceId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    // If the user is not an admin user, check if they have the EDIT_WORKSPACE permission
    return this.editableWorkspaceIds.has(workspaceId)
  }

  canUploadDataToTable(table: TableForCsvUploadPermission): boolean {
    if (this.isAdmin) {
      return true
    }
    const tablePerms = this.tablePermissions.get(table.id)
    if (!tablePerms) {
      return false
    }

    // Must have ADD_ROW permission
    if (!tablePerms.canAddRow) {
      return false
    }

    // Check if user has EDIT_ALL_TABLE_CELLS
    if (tablePerms.hasEditAllCells) {
      return true
    }

    // Match CSV bulk insert: only columns included in Create uploads need EDIT_CELL.
    // Excludes formula columns and index-autogenerated columns (same as getColumnsForCsvUpload).
    const csvWritableColumns = getColumnsForCsvUpload(table.columns, 'Create')
    return csvWritableColumns.every((column) => this.editableColumnIds.has(column.id))
  }

  canConfigureTableTabs(tableId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    return tablePerms.canConfigureTableTabs
  }

  canConfigurePivotTables(tableId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    return tablePerms.canConfigurePivotTables
  }

  canConfigureChart(chart: ConfigurableChart): boolean {
    if (this.isAdmin) {
      return true
    }

    // First check if the user has VIEW_ALL_TABLE_CELLS permission for the chart's table
    const tablePerms = this.tablePermissions.get(chart.table.id)
    if (!tablePerms?.hasViewAllCells) {
      return false
    }

    // Return true if any of the chart's views' parent workspaces are editable
    return chart.views.some((view) => this.editableWorkspaceIds.has(view.parentWorkspace.id))
  }

  canViewTableColumn(tableId: string, columnId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    // Check if user has view all cells permission on the table
    if (tablePerms.hasViewAllCells) {
      return true
    }

    // Check if user has edit all cells permission (implies view permission)
    if (tablePerms.hasEditAllCells) {
      return true
    }

    // Check if user has specific view permission for this column
    return tablePerms.viewableColumnIds.has(columnId)
  }

  canViewTable(tableId: string): boolean {
    if (this.isAdmin) {
      return true
    }

    const tablePerms = this.tablePermissions.get(tableId)
    if (!tablePerms) {
      return false
    }

    // Check if user has view all cells permission on the table
    if (tablePerms.hasViewAllCells) {
      return true
    }

    // Check if user has edit all cells permission (implies view permission)
    if (tablePerms.hasEditAllCells) {
      return true
    }

    // Check if user has at least one viewable column AND one viewable tab
    return tablePerms.viewableColumnIds.size > 0 && tablePerms.viewableTabIds.size > 0
  }
}
